import {
  AssetCategory,
  AssetCategoryIconStyle,
  PredefinedAssetCategory,
  PredefinedAssetCategoryKeys
} from './asset-category.model';

/** Icon identifiers rendered by `sim-icon` for asset categories. */
export type AssetCategoryIconName =
  | 'multimedia'
  | 'homeAppliances'
  | 'officeEquipment'
  | 'furniture'
  | 'decoration'
  | 'lightingSystems'
  | 'plants'
  | 'documents'
  | 'electrical'
  | 'security'
  | 'fireSafety'
  | 'plumbing'
  | 'sanitaryAndHygiene'
  | 'hvacSystems'
  | 'res'
  | 'hobby'
  | 'transport'
  | 'education'
  | 'tools'
  | 'garden'
  | 'machines'
  | 'spareParts'
  | 'pets'
  | 'kids'
  | 'finishingMaterials'
  | 'construction'
  | 'assetOtherIcon';

export const PREDEFINED_ASSET_CATEGORY_ICON: Record<PredefinedAssetCategoryKeys, AssetCategoryIconName> = {
  None: 'assetOtherIcon',
  Multimedia: 'multimedia',
  HomeAppliances: 'homeAppliances',
  OfficeEquipment: 'officeEquipment',
  Furniture: 'furniture',
  Decoration: 'decoration',
  LightingSystems: 'lightingSystems',
  Plants: 'plants',
  Documents: 'documents',
  Electrical: 'electrical',
  Security: 'security',
  FireSafety: 'fireSafety',
  Plumbing: 'plumbing',
  SanitaryAndHygiene: 'sanitaryAndHygiene',
  HVACSystems: 'hvacSystems',
  RES: 'res',
  Hobby: 'hobby',
  Transport: 'transport',
  Education: 'education',
  Tools: 'tools',
  Garden: 'garden',
  Machines: 'machines',
  SpareParts: 'spareParts',
  Pets: 'pets',
  Kids: 'kids',
  FinishingMaterials: 'finishingMaterials',
  Construction: 'construction',
  Other: 'assetOtherIcon'
};

export const PREDEFINED_ASSET_CATEGORY_LABEL: Record<PredefinedAssetCategoryKeys, string> = {
  None: '',
  Multimedia: $localize`:@@MULTIMEDIA:Multimedia`,
  HomeAppliances: $localize`:@@HOME_APPLIANCES:Home Appliances`,
  OfficeEquipment: $localize`:@@OFFICE_EQUIPMENT:Office Equipment`,
  Furniture: $localize`:@@FURNITURE:Furniture`,
  Decoration: $localize`:@@DECORATION:Decoration`,
  LightingSystems: $localize`:@@LIGHTING_SYSTEMS:Lighting Systems`,
  Plants: $localize`:@@PLANTS:Plants`,
  Documents: $localize`:@@DOCUMENTS:Documents`,
  Electrical: $localize`:@@ELECTRICAL:Electrical`,
  Security: $localize`:@@SECURITY:Security`,
  FireSafety: $localize`:@@FIRE_SAFETY:Fire Safety`,
  Plumbing: $localize`:@@PLUMBING:Plumbing`,
  SanitaryAndHygiene: $localize`:@@SANITARY_AND_HYGIENE:Sanitary and Hygiene`,
  HVACSystems: $localize`:@@HVAC_SYSTEMS:HVAC Systems`,
  RES: $localize`:@@RES:RES (Renewable Energy Sources)`,
  Hobby: $localize`:@@HOBBY:Hobby`,
  Transport: $localize`:@@TRANSPORT:Transport`,
  Education: $localize`:@@EDUCATION:Education`,
  Tools: $localize`:@@TOOLS:Tools`,
  Garden: $localize`:@@GARDEN:Garden`,
  Machines: $localize`:@@MACHINES:Machines`,
  SpareParts: $localize`:@@SPARE_PARTS:Spare Parts`,
  Pets: $localize`:@@PETS:Pets`,
  Kids: $localize`:@@KIDS:Kids`,
  FinishingMaterials: $localize`:@@FINISHING_MATERIALS:Finishing Materials`,
  Construction: $localize`:@@CONSTRUCTION:Construction`,
  Other: $localize`:@@OTHER:Other`
};

const PREDEFINED_NAME_BY_VALUE: Record<number, PredefinedAssetCategoryKeys> = Object.fromEntries(
  Object.entries(PredefinedAssetCategory).map(([name, value]) => [value, name])
) as Record<number, PredefinedAssetCategoryKeys>;

/** The API serializes some enums as string names — accepts both `27` and `"Other"`. */
export function resolvePredefinedAssetCategoryName(
  value: number | string | null | undefined
): PredefinedAssetCategoryKeys | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    if (value in PredefinedAssetCategory) return value as PredefinedAssetCategoryKeys;
    return PREDEFINED_NAME_BY_VALUE[Number(value)] ?? null;
  }
  return PREDEFINED_NAME_BY_VALUE[value] ?? null;
}

/**
 * Blob path of the custom category image / uploaded SVG icon, or `null` when the category has none.
 * Some endpoints (asset list, create/update asset responses) return the category without
 * `imageUrlWithSasToken`; the blob path still identifies the image so the url can be resolved client-side.
 */
export function assetCategoryImagePath(category: AssetCategory | null | undefined): string | null {
  return category?.image?.blobPath ?? null;
}

/** `true` when the category carries a custom image, whether or not the download url has been resolved yet. */
export function hasAssetCategoryImage(category: AssetCategory | null | undefined): boolean {
  return !!(category?.imageUrlWithSasToken || assetCategoryImagePath(category));
}

/** Builds a local placeholder for a predefined category (e.g. for markers created before the backend assigns one). */
export function createPredefinedAssetCategory(name: PredefinedAssetCategoryKeys): AssetCategory {
  return {
    id: '',
    apartmentId: '',
    name: null,
    iconStyle: AssetCategoryIconStyle.BlackWhite,
    icon: PREDEFINED_ASSET_CATEGORY_ICON[name],
    iconColor: null,
    iconColorBackground: null,
    imageUrlWithSasToken: null,
    imageId: null,
    image: null,
    scale: null,
    predefinedCategory: PredefinedAssetCategory[name],
    isPredefined: true
  };
}

/** Display label for a category: backend name when set, localized predefined label otherwise. */
export function resolveAssetCategoryLabel(category: AssetCategory): string {
  if (category.name) return category.name;
  const name = resolvePredefinedAssetCategoryName(category.predefinedCategory);
  return name ? PREDEFINED_ASSET_CATEGORY_LABEL[name] : '';
}
